import { gql, useLazyQuery } from "@apollo/client";
import type { ICustomerOrdersQuery, IOrdersEdges, IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import { totalPaidAmountQuery } from "@repo/shopify/src/queries/customer-gql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FinancialStatus } from "../src/utils/order";

type TLanguage = "ko" | "en";

interface IProps {
  accessToken: string;
  startDate: Date;
  endDate: Date;
  lang: TLanguage;
}

// TODO: Shopify에서 쿼리 키로 주문 필터 시 발생하는 에러가 해결되면, 해당 방식으로 주문을 필터링하여 조회할 예정
const useOrdersAmount = ({ accessToken, startDate, endDate, lang }: IProps) => {
  // 모든 주문을 저장할 상태
  const [allOrders, setAllOrders] = useState<IOrdersEdges["edges"] | []>([]);
  // 전체 주문 데이터를 가져오는 중인지 여부
  const [isLoading, setIsLoading] = useState(false);

  // 전체 데이터를 가져오기 위한 쿼리
  const [fetchCustomerOrders] = useLazyQuery<ICustomerOrdersQuery>(gql(totalPaidAmountQuery.query), {
    fetchPolicy: "no-cache",
    onError: (error) => {
      setIsLoading(false);
      throw new Error(`[SHOPIFY][Customer][Orders] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  // 모든 주문을 한 번에 가져오는 함수
  const getAllOrders = useCallback(async () => {
    try {
      setIsLoading(true);

      let hasMorePages = true;
      let paginationCursor = null;
      let allCollectedOrders: IOrdersEdges["edges"] = [];

      // 첫 요청은 최대 250개 가져오기
      const first = 250;

      while (hasMorePages) {
        // eslint-disable-next-line no-await-in-loop
        const { data: orderData } = await fetchCustomerOrders({
          variables: {
            customerAccessToken: accessToken,
            first,
            after: paginationCursor,
            sortKey: "PROCESSED_AT",
            reverse: true,
            // query: `processed_at:>=2024-04-01 AND processed_at:<=2024-12-23`,
          },
        });

        console.log(
          `useOrdersAmount for ${orderData?.customer.email || ""} ${startDate.toISOString()} ${endDate.toISOString()} at ${new Date().toISOString()}:`,
          orderData,
        );

        if (!orderData?.customer.orders) {
          break;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const currentOrders = orderData.customer.orders.edges || [];

        allCollectedOrders = [...allCollectedOrders, ...currentOrders];

        // 다음 페이지 확인
        hasMorePages = orderData.customer.orders.pageInfo.hasNextPage;
        paginationCursor = orderData.customer.orders.pageInfo.endCursor;
      }

      setAllOrders(allCollectedOrders);
    } catch (error) {
      throw new Error(`전체 주문 데이터 가져오기 실패: ${JSON.stringify(error)}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => void getAllOrders(), []);

  // 실주문금액 계산
  const totalOrderAmount = useMemo(() => {
    if (!allOrders.length) return 0;

    return allOrders
      .filter((edge: { node: IOrdersEdgesNode }) => {
        // 날짜 필터
        const processedAtDate = new Date(edge.node.processedAt);
        const isWithinDateRange = processedAtDate >= startDate && processedAtDate <= endDate;

        // FinancialStatus 필터
        const isValidFinancialStatus =
          edge.node.financialStatus !== FinancialStatus.Pending && edge.node.financialStatus !== FinancialStatus.Voided;

        return isWithinDateRange && isValidFinancialStatus;
      })
      .reduce((total, item) => {
        // KR일때
        if (lang === "ko") {
          const subTotalPrice = parseFloat(item.node.subtotalPrice?.amount ?? "0");
          const totalRefunded = parseFloat(item.node.totalRefunded.amount);
          return total + (subTotalPrice - totalRefunded);
        }

        // TODO: INTL일때 어떻게 계산할지 처리 필요
        // YES - currentTotalPrice 합 (kr,intl 둘다)
        // TNT - subTotalPrice - totalRefunded 합 (kr,intl 둘다)
        // Khakis - currentTotalPrice 합 (kr,intl 둘다)

        return total;
      }, 0);
  }, [allOrders, startDate, endDate]);

  console.log(`useOrdersAmount for totalOrderAmount:`, totalOrderAmount);

  return {
    // 모든 주문을 한 번에 가져오는 함수
    getAllOrders,
    // 기간내 실 주문금액 계산
    totalOrderAmount,
    isOrdersLoading: isLoading,
  };
};

export default useOrdersAmount;
